import type { FC } from 'react';
import { Drawer, Button } from 'antd';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import type { BlogArticle } from './BlogArticleTable';
import SeoPreview from './SeoPreview';
import SeoSuggestionsPanel from './SeoSuggestionsPanel';
import BlogEditorForm from './BlogEditorForm';
import BlogGenerationPipeline from './BlogGenerationPipeline';
import BlogSeoPanel from './BlogSeoPanel';
import BlogImagePanel from './BlogImagePanel';
import { useBlogEditorLogic } from './useBlogEditorLogic';

const LANDING_URL = import.meta.env.VITE_LANDING_URL || 'https://feedbackorchestrator.com';

interface BlogEditorDrawerProps {
  open: boolean;
  article: BlogArticle | null;
  onClose: () => void;
}

const BlogEditorDrawer: FC<BlogEditorDrawerProps> = ({ open, article: initialArticle, onClose }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Rédigez votre article ici, ou générez le contenu avec l'IA ✨" }),
    ],
    content: '',
    editorProps: { attributes: { class: 'prose prose-sm dark:prose-invert max-w-none min-h-[200px] outline-none px-3 py-2' } },
  });

  const state = useBlogEditorLogic(open, initialArticle, editor, onClose);

  return (
    <Drawer
      title={state.isEdit ? "Modifier l'article" : 'Nouvel article'}
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Fermer</Button>
          <Button onClick={() => state.handleSave(false)} loading={state.saving} disabled={state.isGenerating}>
            Sauvegarder brouillon
          </Button>
          <Button type="primary" onClick={() => state.handleSave(true)} loading={state.saving} disabled={state.isGenerating}>
            Publier
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <BlogEditorForm
          title={state.title}
          slug={state.slug}
          locale={state.locale}
          persona={state.persona}
          customPersona={state.customPersona}
          autoTranslate={state.autoTranslate}
          isEdit={state.isEdit}
          translating={state.translating}
          detailLoading={state.detailLoading}
          otherLocaleLabel={state.otherLocaleLabel}
          landingUrl={LANDING_URL}
          editor={editor}
          initialArticle={initialArticle}
          onTitleChange={state.setTitle}
          onSlugChange={state.setSlug}
          onLocaleChange={state.setLocale}
          onPersonaChange={state.setPersona}
          onCustomPersonaChange={state.setCustomPersona}
          onAutoTranslateChange={state.setAutoTranslate}
          onTranslateOrSync={() => state.handleTranslateOrSync('sync')}
        />

        <BlogGenerationPipeline
          generationStep={state.generationStep}
          autoTranslate={state.autoTranslate}
          isGenerating={state.isGenerating}
          title={state.title}
          effectiveId={state.effectiveId}
          loadingSeo={state.loadingSeo}
          onGenerateAI={state.handleGenerateAI}
          onSeoSuggestions={state.handleSeoSuggestions}
        />

        <BlogImagePanel
          effectiveId={state.effectiveId}
          featuredImageUrl={state.featuredImageUrl}
          title={state.title}
          seoKeywords={state.seoKeywords}
          imagePreviewOpen={state.imagePreviewOpen}
          onFeaturedImageUrlChange={state.setFeaturedImageUrl}
          onImageGenerated={(url) => {
            state.setFeaturedImageUrl(url);
            state.refreshData();
          }}
          onImagePreviewClose={() => state.setImagePreviewOpen(false)}
        />

        <BlogSeoPanel
          seoTitle={state.seoTitle}
          seoDescription={state.seoDescription}
          seoKeywords={state.seoKeywords}
          excerpt={state.excerpt}
          onSeoTitleChange={state.setSeoTitle}
          onSeoDescriptionChange={state.setSeoDescription}
          onSeoKeywordsChange={state.setSeoKeywords}
          onExcerptChange={state.setExcerpt}
        />

        <SeoPreview title={state.title} slug={state.slug} seoTitle={state.seoTitle} seoDescription={state.seoDescription} />

        {state.suggestions && (
          <SeoSuggestionsPanel
            suggestions={state.suggestions}
            onAddKeyword={(kw) => state.setSeoKeywords((prev) => (prev.includes(kw) ? prev : [...prev, kw]))}
            onApplyTitle={state.setSeoTitle}
            onApplyDescription={state.setSeoDescription}
          />
        )}
      </div>
    </Drawer>
  );
};

export default BlogEditorDrawer;
